<template>
  <section class="container">
    <div
      v-if="isReady"
      class="row mb-3"
    >
      <div class="col-12">
        <div
          v-if="isInAmendmentStatus"
          class="fixed-bottom wrapper-amendment-buttons"
        >
          <div class="d-flex flex-column">
            <p
              v-if="isAmendmentCancelled"
              class="text-center small mb-2"
              style="color: #6c757d;"
            >
              Cet avenant a été annulé.
            </p>
            <p
              v-else-if="isAmendmentValidated"
              class="text-center small mb-2"
              style="color: #6c757d;"
            >
              Avenant en préparation
            </p>
            <v-loader
              id="cancel-amendment-button"
              :next="openDeleteAmendmentModal"
              :disable-button="isAmendmentCancelled"
              color="pink-700"
              title="Annuler l'avenant"
            />
            <v-loader
              id="open-amendment-summary-button"
              :next="goToAmendmentSummary"
              :disable-button="isAmendmentCancelled"
              title="Voir le récapitulatif"
              icon="arrow-white-right"
              class="mt-2"
            />
          </div>
        </div>
        <div
          v-else-if="hasPendingValidatedAmendment"
          class="fixed-bottom wrapper-amendment-buttons"
        >
          <div class="d-flex flex-column">
            <p
              class="text-center small mb-2"
              style="color: #6c757d;"
            >
              Avenant en préparation
            </p>
            <v-loader
              id="cancel-amendment-button"
              :next="openDeleteAmendmentModal"
              color="pink-700"
              title="Annuler l'avenant"
            />
            <v-loader
              id="open-amendment-summary-button"
              :next="openPendingAmendmentSummary"
              title="Voir le récapitulatif"
              icon="arrow-white-right"
              class="mt-2"
            />
          </div>
        </div>
        <div
          v-else-if="hasPendingAmendment"
          class="fixed-bottom wrapper-amendment-buttons"
        >
          <div class="d-flex flex-column">
            <v-loader
              id="resume-amendment-button"
              :next="openPendingAmendment"
              title="Reprendre l'avenant"
              icon="pen-white"
            />
          </div>
        </div>
        <div
          v-else-if="shouldShowCalendly"
          class="fixed-bottom wrapper-amendment-buttons"
        >
          <div class="d-flex flex-column">
            <v-loader
              title="Modifier mon contrat"
              icon="pen-white"
              :next="openCalendly"
            />
          </div>
        </div>
        <div
          v-else-if="canCreateAmendment"
          class="fixed-bottom wrapper-amendment-buttons"
        >
          <div class="d-flex flex-column">
            <v-loader
              id="create-amendment-button"
              :next="createAmendmentVersion"
              :force-loader="isCreatingAmendment"
              title="Modifier mon contrat"
              icon="pen-white"
            />
          </div>
        </div>
      </div>
      <div class="col-12">
        <div class="row mb-5">
          <div class="col-2">
            <v-icon
              icon="chevron-left"
              class="cursor-pointer"
              @click="() => $router.back()"
            />
          </div>
          <div class="col-8 text-center">
            <h1>{{ getProductName }}</h1>
            <div class="mb-1">
              N° {{ contract.reference }}
            </div>
            <div
              class="badge badge-pill rounded-pill"
              :class="`bg-${formattedStatus.color} text-${formattedStatus.textColor || 'blue-700'}`"
            >
              {{ formattedStatus.text }}
            </div>
            <div class="mt-1">
              <v-amendment-badge :contract="contract" />
            </div>
          </div>
          <div
            v-if="contract.documents && contract.documents.length > 0"
            class="col-2 text-center"
          >
            <router-link
              v-slot="{ navigate }"
              :to="`/client/documents/${contract.productId}/${contract.id}`"
              custom
            >
              <div>
                <div
                  class="rounded-circle bg-blue-700 d-flex mx-auto cursor-pointer"
                  style="height: 45px; width: 45px; min-width: 45px"
                  @click="navigate"
                >
                  <div class="d-flex m-auto">
                    <v-icon
                      folder="common/svg"
                      icon="folder-white"
                      width="24"
                      height="24"
                    />
                  </div>
                </div>
                <div
                  class="fw-bold mt-1 text-blue-700 cursor-pointer d-md-inline-block d-none"
                  @click="navigate"
                >
                  Vos documents
                </div>
              </div>
            </router-link>
          </div>
        </div>
      </div>

      <div class="col-12">
        <div
          v-if="isArchivedVersion"
          class="alert alert-info mb-4"
          role="alert"
        >
          Cette version archivée est disponible en lecture seule.
        </div>
        <div
          v-if="showMissingInfoWarning"
          class="card mb-4 mx-auto shadow-sm border-warning"
        >
          <div
            class="card-body d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3"
          >
            <div class="d-flex align-items-center justify-content-center flex-shrink-0">
              <v-icon
                icon="warning"
                width="32"
                height="32"
                class="text-warning"
              />
            </div>
            <div class="flex-grow-1 text-center text-md-start">
              <div class="lead fw-bold mb-1">
                Informations manquantes
              </div>
              <div class="text-muted">
                Certaines informations sont manquantes. Complétez-les pour finaliser votre dossier.
              </div>
            </div>
            <div class="text-center text-md-end w-100 w-md-auto">
              <button
                type="button"
                class="btn btn-primary mt-3 mt-md-0"
                @click="openComplementaryInfoModal"
              >
                Compléter mes informations
              </button>
            </div>
          </div>
        </div>
        <v-section-large />
        <div class="my-3 text-center lead fw-bold">
          Informations complémentaires
        </div>
        <v-section-small />
        <div
          v-show="haveExtensions"
          class="my-3 text-center lead fw-bold"
        >
          Garanties optionnelles
        </div>
        <v-extensions />
      </div>
      <div
        v-if="hasFloatingActions"
        class="py-md-6 py-5"
      />
    </div>
    <v-loading-box
      v-else
      class="mt-5"
    />
  </section>
</template>
<script>
/* eslint-disable max-lines */
import { mapActions, mapGetters } from 'vuex';
import { get } from 'lodash-es';
import { ui } from '@/mixins';

import Extensions from '@/components/contracts/helpers/ContractExtensionsSection.vue';
import ContractLargeCardsSection from '@/components/contracts/sections/ContractLargeCardsSection.vue';
import ContractSmallCardsSection from '@/components/contracts/sections/ContractSmallCardsSection.vue';
import LoadingAnimation from '@/components/common/helpers/LoadingAnimation.vue';
import AmendmentBadge from '@/components/contracts/AmendmentBadge.vue';
/* eslint-disable object-curly-newline */

export default {
  name: 'ContractView',
  components: {
    'v-loading-box': LoadingAnimation,
    'v-section-small': ContractSmallCardsSection,
    'v-section-large': ContractLargeCardsSection,
    'v-extensions': Extensions,
    'v-amendment-badge': AmendmentBadge,
  },
  mixins: [ui],
  data() {
    return {
      isReady: false,
      status: [
        { id: 'ACTIVE', text: 'Actif', color: 'green-300' },
        { id: 'SUSPENDED', text: 'Suspendu', color: 'gray-300' },
        { id: 'AMENDMENT', text: 'Avenant', color: 'yellow-300' },
        { id: 'ARCHIVED', text: 'Archivé', color: 'yellow-300' },
        { id: 'CANCELLED', text: 'Résilié', color: 'pink-700', textColor: 'white' },
        { id: 'EXPIRED', text: 'Expiré', color: 'pink-700', textColor: 'white' },
      ],
    };
  },
  computed: {
    ...mapGetters({
      contract: 'contract/get',
      policyContext: 'contract/policyContext',
      pendingAmendment: 'contract/pendingAmendment',
      isCreatingAmendment: 'contract/isCreatingAmendment',
      products: 'rater/products',
    }),
    getInsurerId() {
      return this.contract.insurer?.id;
    },
    getMainActivityId() {
      return get(this.contract, 'risk.mainActivity.id', null);
    },
    getProductName() {
      return (this.products.find(({ id }) => id === this.contract.productId) || {}).name;
    },
    showMissingInfoWarning() {
      //only show for markel contracts (ID: 39)
      if (this.getInsurerId !== 39) {
        return false;
      }

      const activityId = this.getMainActivityId;
      if (!activityId) {
        return false;
      }
      // check information missing
      const risk = get(this.contract, 'risk', {});
      //Expert comptable - ID: 10
      if (activityId === 10) {
        return !risk.accountantNumber || !risk.accountantRegistrationDate;
      }

      // CAC - ID: 3
      if (activityId === 3) {
        return !risk.auditorNumber || !risk.accountantRegistrationDate
          || !risk.accountantNumber;
      }

      //Géomètre expert - ID: 7
      if (activityId === 7) {
        return !risk.surveyorNumber;
      }

      // agent immobilier - ID: 13
      // administrateur de biens - ID: 4
      if (activityId === 13 || activityId === 4) {
       return !risk.professionalCard
          || !risk.professionalCard.cardNumber
          || !risk.professionalCard.approvers
          || risk.professionalCard.approvers.length === 0;
      }

      // Activities requiring ORIAS number
      const oriasActivityIds = [1, 2, 8, 12, 15, 16, 17, 18, 719];
      if (oriasActivityIds.includes(activityId)) {
        return !risk.oriasNumber;
      }
      return false;
    },
    formattedStatus() {
      if (this.isAmendmentValidated) {
        return Object.assign({}, this.status.find(({ id }) => id === this.contract.status), {
          text: 'Avenant en préparation',
        });
      }
      return this.status.find(({ id }) => id === this.contract.status) || {};
    },
    resolvedPolicyId() {
      return this.policyContext && this.policyContext.id
        ? this.policyContext.id
        : (this.contract.policyId || (this.contract.activeVersionId ? this.contract.id : null));
    },
    isInAmendmentStatus() {
      return this.contract.status === 'AMENDMENT';
    },
    isArchivedVersion() {
      return this.contract.status === 'ARCHIVED';
    },
    isAmendmentCancelled() {
      return this.contract.status === 'AMENDMENT' && this.contract.leadStatus === 'CANCELLED';
    },
    isAmendmentValidated() {
      return this.contract.status === 'AMENDMENT' && this.contract.leadStatus === 'AMENDMENT_VALIDATED';
    },
    hasPendingAmendment() {
      return Boolean(this.pendingAmendment && this.pendingAmendment.id !== this.contract.id);
    },
    hasPendingValidatedAmendment() {
      return this.hasPendingAmendment && this.pendingAmendment.leadStatus === 'AMENDMENT_VALIDATED';
    },
    canCreateAmendment() {
      return ['ACTIVE', 'SUSPENDED'].includes(this.contract.status)
        && Boolean(this.resolvedPolicyId)
        && !this.hasPendingAmendment
        && !this.shouldShowCalendly;
    },
    shouldShowCalendly() {
      return !this.isInAmendmentStatus
        && !this.isArchivedVersion
        && (this.contract.derogation || !this.contract.delegation);
    },
    hasFloatingActions() {
      return this.isInAmendmentStatus || this.hasPendingAmendment || this.canCreateAmendment || this.shouldShowCalendly;
    },
    haveExtensions() {
      const extensions = get(this, 'contract.extensions', []).filter(({ visible }) => visible);
      return extensions.length > 0;
    },
  },
  watch: {
    '$route.params.id': {
      handler: 'loadContract',
      immediate: true,
    },
  },
  mounted() {
    this.ui_showMenu(true);
  },
  beforeUnmount() {
    if (this.isInAmendmentStatus) {
      this.$store.dispatch('contracts/updateSubscription', this.contract);
    }
  },
  methods: {
    ...mapActions({
      openCalendly: 'ui/openCalendly',
    }),
    loadContract(id) {
      this.isReady = false;
      return this.$store.dispatch('contract/getContractDetail', {
        id,
        entity: this.$route.query.entity || null,
      })
        .catch(() => { this.$router.replace('/failure') })
        .finally(() => {
          setTimeout(() => { this.isReady = true }, 600);
        });
    },
    goToAmendmentSummary() {
      const isValid = document.querySelector('.is-invalid') === null;
      if (!isValid) {
        document.querySelector('.is-invalid').scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        return Promise.resolve();
      }
      if (this.shouldShowCalendly) {
        return this.openCalendly();
      }
      this.ui_showModal(false);
      return this.$router.push({
        path: `/client/contracts/${this.contract.id}/summary`,
        query: {
          entity: 'subscription',
          amendmentId: this.contract.id,
        },
      });
    },
    async createAmendmentVersion() {
      try {
        const amendment = await this.$store.dispatch('contract/createAmendment', this.resolvedPolicyId);
        if (!amendment || !amendment.id) {
          this.ui_showToast({ show: true, message: 'Impossible de créer un avenant pour le moment.' });
          return Promise.resolve();
        }

        return this.$router.push({
          path: `/client/contracts/${amendment.id}`,
          query: { entity: 'subscription' },
        });
      } catch (error) {
        this.ui_showToast({ show: true, message: 'Impossible de créer un avenant pour le moment.' });
        throw error;
      }
    },
    openPendingAmendment() {
      if (!this.pendingAmendment || !this.pendingAmendment.id) {
        return Promise.resolve();
      }

      return this.$router.push({
        path: `/client/contracts/${this.pendingAmendment.id}`,
        query: { entity: 'subscription' },
      });
    },
    openPendingAmendmentSummary() {
      if (!this.pendingAmendment || !this.pendingAmendment.id) {
        return Promise.resolve();
      }

      return this.$router.push({
        path: `/client/contracts/${this.pendingAmendment.id}/summary`,
        query: {
          entity: 'subscription',
          amendmentId: this.pendingAmendment.id,
        },
      });
    },
    openDeleteAmendmentModal() {
      this.ui_showModalAmendment(null, true);
      return Promise.resolve();
    },
    openModalEmployeeTravelModal() {
      this.$store.dispatch('employee/clear');
      this.ui_showModalAmendment('employee-travel', true);
      return Promise.resolve();
    },
    openComplementaryInfoModal() {
      // Use the dedicated ComplementaryInformationModal
      this.ui_showModalComplementaryInfo();
      return Promise.resolve();
    },
  },
};
</script>
<style scoped>
.wrapper-amendment-buttons {
  margin-right: 100px;
  margin-left: auto;
  width: 350px;
  bottom: 25px;
  z-index: 40;
}
@media screen and (max-width: 992px) {
  .wrapper-amendment-buttons {
    padding: 15px;
    bottom: 80px;
  }
}
@media screen and (max-width: 576px) {
  .wrapper-amendment-buttons {
    margin-right: unset;
    margin-left: unset;
    width: 100%;
    padding: 15px;
    bottom: 25px;
  }
}
</style>
